import React from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { Button, Descriptions, Drawer } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDate, formatTime } from '../clientCard.format';
import { CardVisit, FOLDER_BUCKET_LABEL_KEYS, FolderFile } from '../clientCard.model';
import { FileThumb } from '../ClientFolderPanel';
import { cardDrawerWidth } from './VisitDrawer';

type AttachmentDrawerProps = {
  file: FolderFile | null;
  /** The window's visits, so a file from one can link back to it without a fetch. */
  visits: CardVisit[];
  onClose: () => void;
  onOpenVisit: (visit: CardVisit) => void;
  onDownload: (file: FolderFile) => void;
  isDownloading: boolean;
};

/**
 * One file: what it is, when it arrived, which visit produced it, and a download.
 *
 * The thumbnail is shown when the API sent one and a placeholder otherwise. The
 * drawer deliberately does not fetch the file to preview it — a folder of scans is
 * megabytes, and the reader who wants the contents downloads it.
 */
const AttachmentDrawer: React.FC<AttachmentDrawerProps> = ({
  file,
  visits,
  onClose,
  onOpenVisit,
  onDownload,
  isDownloading,
}) => {
  const { t, i18n } = useTranslation();

  const source = file?.source;
  const sourceVisit =
    source && source.type === 'visit' ? (visits.find((visit) => visit.id === source.visitId) ?? null) : null;

  return (
    <Drawer
      open={!!file}
      onClose={onClose}
      width={cardDrawerWidth(560)}
      title={file ? file.attachment.description || file.attachment.filename : ''}
    >
      {file && (
        <div className="flex flex-col gap-4">
          {/* One height either way: the thumbnail may arrive after the drawer opens
              (visit files fetch theirs), and a box that resizes under the reader is
              worse than a slightly tall placeholder. */}
          <FileThumb file={file} height={240} />

          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              { key: 'filename', label: t('client-card-file-name'), children: file.attachment.filename },
              {
                key: 'kind',
                label: t('client-card-file-kind'),
                children: t(FOLDER_BUCKET_LABEL_KEYS[file.bucket]),
              },
              {
                key: 'uploaded',
                label: t('client-card-file-uploaded'),
                children: `${formatDate(file.at, i18n.language)} ${formatTime(file.at, i18n.language)}`,
              },
              ...(file.attachment.description
                ? [
                    {
                      key: 'description',
                      label: t('client-card-file-description'),
                      children: file.attachment.description,
                    },
                  ]
                : []),
              {
                key: 'source',
                label: t('client-card-file-source'),
                children: sourceVisit ? (
                  <a onClick={() => onOpenVisit(sourceVisit)}>
                    {`${formatDate(sourceVisit.startTime, i18n.language)} ${formatTime(
                      sourceVisit.startTime,
                      i18n.language
                    )}`}
                  </a>
                ) : (
                  t('client-card-folder-from-client')
                ),
              },
            ]}
          />

          <div>
            <Button type="primary" icon={<DownloadOutlined />} loading={isDownloading} onClick={() => onDownload(file)}>
              {t('client-card-file-download')}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default AttachmentDrawer;
